import moment from "moment";

const COMPANY = {
  name: "ROYAL COMMERCE CLASS",
  addressLine: "50/225 HALSEY ROAD,",
  city: "KANPUR",
  email: "ROYALCOMMERCECLASS@GMAIL.COM",
  phone: "7668555614",
  gstin: "09BBYPG9864J1ZJ",
  signatory: "SUNIT GUPTA",
};

const HSN_CODE = "99923";
const CGST_RATE = 9;
const SGST_RATE = 9;

const STATE_CODES: Record<string, string> = {
  "jammu and kashmir": "01",
  "himachal pradesh": "02",
  punjab: "03",
  chandigarh: "04",
  uttarakhand: "05",
  haryana: "06",
  delhi: "07",
  rajasthan: "08",
  "uttar pradesh": "09",
  bihar: "10",
  sikkim: "11",
  "arunachal pradesh": "12",
  nagaland: "13",
  manipur: "14",
  mizoram: "15",
  tripura: "16",
  meghalaya: "17",
  assam: "18",
  "west bengal": "19",
  jharkhand: "20",
  odisha: "21",
  orissa: "21",
  chhattisgarh: "22",
  "madhya pradesh": "23",
  gujarat: "24",
  "dadra and nagar haveli and daman and diu": "26",
  maharashtra: "27",
  karnataka: "29",
  goa: "30",
  lakshadweep: "31",
  kerala: "32",
  "tamil nadu": "33",
  puducherry: "34",
  "andaman and nicobar islands": "35",
  telangana: "36",
  "andhra pradesh": "37",
  ladakh: "38",
};

const round2 = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

const formatAmount = (value: number) =>
  round2(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const splitInclusive = (inclusive: number) => {
  const taxable = round2(inclusive / 1.18);
  const cgst = round2((taxable * CGST_RATE) / 100);
  const sgst = round2(inclusive - taxable - cgst);
  return { taxable, cgst, sgst, total: round2(inclusive) };
};

const placeOfSupply = (state?: string) => {
  const name = (state || "").trim();
  if (!name) return "-";
  const code = STATE_CODES[name.toLowerCase()];
  return code ? `${code}-${name}` : name;
};

const MailIcon = () => (
  <svg viewBox="0 0 24 24" className="inline-block h-3.5 w-3.5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" className="inline-block h-3.5 w-3.5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 7a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.6 1 1 0 0 1-.25 1L6.6 10.8z"
    />
  </svg>
);

type Line = {
  description: string;
  hsn: string;
  taxable: number;
  cgstRate: number;
  cgst: number;
  sgstRate: number;
  sgst: number;
};

const buildLines = (receiptData: any, inclusiveTotal: number): Line[] => {
  const products = Array.isArray(receiptData?.products) ? receiptData.products : [];
  const weighted = products
    .map((product: any) => {
      const quantity = Number(product?.quantity) || 1;
      const price = Number(product?.price) || 0;
      return {
        description: product?.productName || product?.name || "VIDEO LECTURES",
        hsn: String(product?.hsn || product?.sac || HSN_CODE),
        weight: price > 0 ? price * quantity : 0,
      };
    })
    .filter((item: { description: string }) => item.description);

  const source = weighted.length
    ? weighted
    : [
        {
          description:
            receiptData?.courseName ||
            receiptData?.name ||
            "VIDEO LECTURES",
          hsn: HSN_CODE,
          weight: inclusiveTotal,
        },
      ];

  const weightSum = source.reduce(
    (sum: number, item: { weight: number }) => sum + item.weight,
    0
  );

  let consumed = 0;
  return source.map((item: { description: string; hsn: string; weight: number }, index: number) => {
    const isLast = index === source.length - 1;
    const share = !weightSum
      ? inclusiveTotal / source.length
      : (item.weight / weightSum) * inclusiveTotal;
    const inclusive = isLast ? round2(inclusiveTotal - consumed) : round2(share);
    consumed += inclusive;
    const tax = splitInclusive(inclusive);
    return {
      description: item.description,
      hsn: item.hsn,
      taxable: tax.taxable,
      cgstRate: CGST_RATE,
      cgst: tax.cgst,
      sgstRate: SGST_RATE,
      sgst: tax.sgst,
    };
  });
};

type Props = {
  receiptData: any;
};

const TaxInvoice = ({ receiptData }: Props) => {
  const logoSrc = `${process.env.PUBLIC_URL}/favicon-32x32.png`;
  const inclusiveTotal =
    Number(receiptData?.totalPayable) ||
    Number(receiptData?.amtAfterDiscount) ||
    Number(receiptData?.orderTotal) ||
    Number(receiptData?.totalFees) ||
    0;
  const lines = buildLines(receiptData, inclusiveTotal);
  const taxableTotal = round2(lines.reduce((sum, line) => sum + line.taxable, 0));
  const cgstTotal = round2(lines.reduce((sum, line) => sum + line.cgst, 0));
  const sgstTotal = round2(lines.reduce((sum, line) => sum + line.sgst, 0));
  const grandTotal = round2(taxableTotal + cgstTotal + sgstTotal);

  const hsnGroups = lines.reduce<Record<string, Line>>((groups, line) => {
    const current = groups[line.hsn] || {
      description: "",
      hsn: line.hsn,
      taxable: 0,
      cgstRate: line.cgstRate,
      cgst: 0,
      sgstRate: line.sgstRate,
      sgst: 0,
    };
    current.taxable = round2(current.taxable + line.taxable);
    current.cgst = round2(current.cgst + line.cgst);
    current.sgst = round2(current.sgst + line.sgst);
    groups[line.hsn] = current;
    return groups;
  }, {});

  const invoiceDate = receiptData?.createdAt || receiptData?.createdOn;
  const parsedDate = invoiceDate ? moment(invoiceDate) : null;
  const billLines = [
    receiptData?.name,
    receiptData?.address,
    receiptData?.city,
    receiptData?.state,
    "India",
    receiptData?.pincode,
  ].filter((line) => line && String(line).trim() && String(line).trim() !== "-");

  return (
    <div className="tax-invoice mx-auto w-full max-w-[860px] bg-white px-6 py-5 text-black">
      <style>{`
        .tax-invoice { font-family: Arial, Helvetica, sans-serif; }
        .tax-sheet { width: 100%; border-collapse: collapse; margin-top: -1px; }
        .tax-sheet:first-of-type { margin-top: 0; }
        .tax-sheet td, .tax-sheet th { border: 1px solid #000; padding: 4px 6px; font-size: 12px; vertical-align: middle; }
        @page { size: A4; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .tax-invoice { max-width: none !important; padding: 0 !important; }
        }
      `}</style>

      <div className="relative mb-3 min-h-[96px] text-center">
        <img
          src={logoSrc}
          alt="RCC Lectures"
          className="absolute left-0 top-0 h-[92px] w-auto object-contain"
        />
        <h1 className="text-[22px] font-bold tracking-wide">{COMPANY.name}</h1>
        <p className="text-[13px] leading-5">{COMPANY.addressLine}</p>
        <p className="text-[13px] leading-5">{COMPANY.city}</p>
        <p className="mt-1 flex items-center justify-center gap-3 text-[12px]">
          <span className="inline-flex items-center gap-1">
            <MailIcon /> {COMPANY.email}
          </span>
          <span className="inline-flex items-center gap-1">
            <PhoneIcon /> {COMPANY.phone}
          </span>
        </p>
        <p className="text-[12px]">GSTIN: {COMPANY.gstin}</p>
      </div>

      <h2 className="mb-2 text-center text-[20px] font-bold">Tax Invoice</h2>

      <table className="tax-sheet">
        <tbody>
          <tr>
            <td className="w-1/2 align-top" rowSpan={3}>
              <p className="font-bold">Bill To:</p>
              {billLines.map((line, index) => (
                <p key={`${line}-${index}`} className="leading-5">
                  {line}
                </p>
              ))}
              {receiptData?.email ? (
                <p className="mt-1 flex items-center gap-1 leading-5">
                  <MailIcon /> {receiptData.email}
                </p>
              ) : null}
              {receiptData?.mobile ? (
                <p className="flex items-center gap-1 leading-5">
                  <PhoneIcon /> {receiptData.mobile}
                </p>
              ) : null}
              <p className="mt-1">
                Place Of Supply: {placeOfSupply(receiptData?.state)}
              </p>
            </td>
            <td className="w-1/4 font-semibold">Invoice No</td>
            <td className="w-1/4 font-semibold">Date</td>
          </tr>
          <tr>
            <td className="font-bold">{receiptData?.orderNumber || "-"}</td>
            <td className="font-bold">
              {parsedDate?.isValid() ? parsedDate.format("DD-MM-YYYY") : "-"}
            </td>
          </tr>
          <tr>
            <td colSpan={2} className="h-16" />
          </tr>
        </tbody>
      </table>

      <table className="tax-sheet border-t-0">
        <thead>
          <tr>
            <th className="w-[36px]">#</th>
            <th>Description</th>
            <th className="w-[70px]">HSN</th>
            <th className="w-[62px]">CGST Rate</th>
            <th className="w-[78px]">CGST Amount</th>
            <th className="w-[62px]">SGST Rate</th>
            <th className="w-[78px]">SGST Amount</th>
            <th className="w-[90px]">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.hsn}-${index}`}>
              <td className="text-center">{index + 1}</td>
              <td className="text-center font-medium uppercase">{line.description}</td>
              <td className="text-center">{line.hsn}</td>
              <td className="text-center">{line.cgstRate}%</td>
              <td className="text-right">{formatAmount(line.cgst)}</td>
              <td className="text-center">{line.sgstRate}%</td>
              <td className="text-right">{formatAmount(line.sgst)}</td>
              <td className="text-right">{formatAmount(line.taxable)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={6} rowSpan={3} />
            <td className="text-right font-semibold">Subtotal</td>
            <td className="text-right font-semibold">{formatAmount(taxableTotal)}</td>
          </tr>
          <tr>
            <td className="text-right">CGST@{CGST_RATE}%</td>
            <td className="text-right">{formatAmount(cgstTotal)}</td>
          </tr>
          <tr>
            <td className="text-right">SGST@{SGST_RATE}%</td>
            <td className="text-right">{formatAmount(sgstTotal)}</td>
          </tr>
          <tr>
            <td colSpan={2} className="font-bold">
              Total
            </td>
            <td colSpan={5} />
            <td className="text-right font-bold">INR{formatAmount(grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      <table className="tax-sheet border-t-0">
        <tbody>
          <tr>
            <td className="font-semibold">HSN Summary</td>
          </tr>
        </tbody>
      </table>
      <table className="tax-sheet border-t-0">
        <thead>
          <tr>
            <th>HSN/SAC</th>
            <th>Taxable Value</th>
            <th>CGST Rate</th>
            <th>CGST Amount</th>
            <th>SGST Rate</th>
            <th>SGST Amount</th>
            <th>Total Tax Amount</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(hsnGroups).map((group) => (
            <tr key={group.hsn}>
              <td className="text-center">{group.hsn}</td>
              <td className="text-right">{formatAmount(group.taxable)}</td>
              <td className="text-center">{group.cgstRate}</td>
              <td className="text-right">{formatAmount(group.cgst)}</td>
              <td className="text-center">{group.sgstRate}</td>
              <td className="text-right">{formatAmount(group.sgst)}</td>
              <td className="text-right">{formatAmount(group.cgst + group.sgst)}</td>
            </tr>
          ))}
          <tr className="font-bold">
            <td>Total</td>
            <td className="text-right">{formatAmount(taxableTotal)}</td>
            <td />
            <td className="text-right">{formatAmount(cgstTotal)}</td>
            <td />
            <td className="text-right">{formatAmount(sgstTotal)}</td>
            <td className="text-right">{formatAmount(cgstTotal + sgstTotal)}</td>
          </tr>
        </tbody>
      </table>

      <table className="tax-sheet border-t-0">
        <tbody>
          <tr>
            <td className="w-[68%] align-top text-[12px] leading-5">
              We declare that this invoice shows the actual price of the goods
              described and that all particulars are true and correct.
            </td>
            <td className="align-bottom text-right">
              <p className="text-[16px] font-semibold tracking-wide">
                {COMPANY.signatory}
              </p>
              <p className="text-[12px]">Authorized Signatory</p>
            </td>
          </tr>
          <tr>
            <td colSpan={2} className="py-2 text-center text-[12px]">
              This is computer generated invoice
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default TaxInvoice;
